import { Row } from '@tanstack/react-table'

import { Loader } from '@/ui/Loader'
import { Table, TableHeader } from '@/ui/Table'
import { CombinedLogRow } from './components/CombinedLogRow'
import { Log } from '@/types/logs'
import { Context } from '../context'

import { useShowSharedLog } from '../hooks/useShowSharedLog'
import { useLeaveSiteAlert } from '../hooks/useLeaveSiteAlert'
import { useCombinedLogsForm } from '../hooks/useCombinedLogsForm'
import { useHandleShiftClick } from '../hooks/useHandleShiftClick'

import styles from './CombinedLogs.module.scss'
import { Fragment, useCallback } from 'react'
import { useSharedLogsTable } from '../hooks/useSharedLogsTable'
import { TabFilters } from './TabFilters/TabFilters'

export const CombinedLogsShared = () => {
    const { table, isLoading, setRowSelection, isManualSelectionEnabled } = useSharedLogsTable()

    const { handleShiftClick, logContainerRef, multipleSelection } = useHandleShiftClick()
    const { sharedLogRef } = useShowSharedLog(isLoading, table, logContainerRef)
    const { form, onSubmit, editWrapperRef, isCreateFormActive, setIsCreateFormActive } = useCombinedLogsForm({
        table,
        setRowSelection,
        isShared: true,
    })

    const handleRowClick = useCallback(
        (row: Row<Log>) => {
            setRowSelection((prev) =>
                isManualSelectionEnabled ? { ...prev, [row.id]: !prev[row.id] } : { [row.id]: true }
            )
        },
        [isManualSelectionEnabled, setRowSelection]
    )

    useLeaveSiteAlert({ table, isCreateFormActive })

    if (isLoading) return <Loader />

    return (
        <Context.Provider value={{ form, setIsCreateFormActive }}>
            <section className={styles.root}>
                <div className={styles.logsContainer}>
                    <TabFilters />
                    <form onSubmit={form.handleSubmit(onSubmit)} className={styles.formLayout}>
                        <input type="submit" hidden />
                        <div data-scrollable-table className={styles.tableContainer} ref={logContainerRef}>
                            <Table className={styles.table}>
                                <TableHeader table={table} />

                                <tbody className={styles.tableBody}>
                                    {table.getRowModel().rows.map((row, rowIndex, array) => (
                                        <Fragment key={row.id}>
                                            <CombinedLogRow
                                                key={row.id}
                                                row={row}
                                                rowIndex={rowIndex}
                                                array={array}
                                                editWrapperRef={editWrapperRef}
                                                sharedLogRef={sharedLogRef}
                                                form={form}
                                                table={table}
                                                multipleSelection={multipleSelection}
                                                setIsCreateFormActive={setIsCreateFormActive}
                                                onClick={handleRowClick}
                                                onShiftClick={(event, rowData) =>
                                                    handleShiftClick(event, rowData as Row<Log>)
                                                }
                                            />
                                        </Fragment>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </form>
                </div>
            </section>
        </Context.Provider>
    )
}
